export const MINI_PROGRAM_CHANNEL = "wechat-mini-program";
export const PRODUCT_BRIDGE_ROUTE = "/pages/product-bridge/index";

type MiniProgramNavigateOptions = {
  url: string;
  fail?: (error: unknown) => void;
  success?: () => void;
};

export type MiniProgramNavigationApi = {
  navigateTo: (options: MiniProgramNavigateOptions) => void;
  navigateBack?: (options?: { delta?: number }) => void;
  reLaunch?: (options: { url: string }) => void;
};

export type ProductNavigationRuntime = {
  search: string;
  miniProgram?: MiniProgramNavigationApi;
  openWindow: (url: string) => void;
};

export type ProductNavigationResult = "browser" | "mini-program" | "unavailable";

export type MiniProgramProductTarget = {
  miniAppId?: string | null;
  miniProgramUrl?: string | null;
};

declare global {
  interface Window {
    wx?: {
      miniProgram?: MiniProgramNavigationApi;
    };
  }
}

export function isMiniProgramChannel(search: string): boolean {
  return new URLSearchParams(search).get("channel") === MINI_PROGRAM_CHANNEL;
}

export function buildProductBridgeUrl(target: MiniProgramProductTarget = {}): string {
  const query = new URLSearchParams({ source: "search-h5" });
  const appId = String(target.miniAppId || "").trim();
  const path = String(target.miniProgramUrl || "").trim();
  if (/^wx[0-9a-fA-F]{16}$/.test(appId) && path.startsWith("/ec_goods/detail?") && path.length <= 512) {
    query.set("targetAppId", appId);
    query.set("targetPath", path);
  }
  return `${PRODUCT_BRIDGE_ROUTE}?${query.toString()}`;
}

function browserRuntime(): ProductNavigationRuntime {
  return {
    search: window.location.search,
    miniProgram: window.wx?.miniProgram,
    openWindow: (url) => window.open(url, "_blank", "noopener,noreferrer"),
  };
}

function isSafeHttpsUrl(value?: string): value is string {
  if (!value) return false;
  try {
    return new URL(value).protocol === "https:";
  } catch {
    return false;
  }
}

/**
 * Mini Program traffic may only enter the native product bridge. It never
 * falls through to an H5 merchant page. Standalone browsers keep the current
 * HTTPS product URL behavior.
 */
export function openProductDestination(
  productUrl?: string,
  target: MiniProgramProductTarget = {},
  runtime: ProductNavigationRuntime = browserRuntime(),
): ProductNavigationResult {
  if (isMiniProgramChannel(runtime.search)) {
    if (!runtime.miniProgram?.navigateTo) return "unavailable";
    const bridgeUrl = buildProductBridgeUrl(target);
    if (!new URLSearchParams(bridgeUrl.split("?")[1]).has("targetAppId")) return "unavailable";
    try {
      runtime.miniProgram.navigateTo({
        url: bridgeUrl,
        fail: () => undefined,
      });
      return "mini-program";
    } catch {
      return "unavailable";
    }
  }

  if (!isSafeHttpsUrl(productUrl)) return "unavailable";
  runtime.openWindow(productUrl);
  return "browser";
}
